import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Picker } from '@/app/components/Picker'
import type { PickerItem } from '@/app/components/Picker'
import { useGenerationFormStore } from '../store'
import { useAvailableModels } from '../hooks'
import { RESOURCE_TYPE } from '../types'
import { IconChevronRight } from '@/app/components/icons'

interface ModelItem extends PickerItem {
  readonly fileName: string
}

function parseModelName(fullPath: string): ModelItem {
  // Model names can be full paths like "sd_xl/realvisxlV40.safetensors"
  // label = just the filename without extension, description = folder path if any
  const parts = fullPath.split('/')
  const fileName = parts[parts.length - 1] ?? fullPath
  const baseName = fileName.replace(/\.(safetensors|ckpt|pt|bin)$/i, '')
  const folder = parts.length > 1 ? parts.slice(0, -1).join('/') : undefined

  return {
    id: fullPath,
    label: baseName,
    description: folder,
    fileName,
  }
}

export function ModelSelect() {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const checkpoint = useGenerationFormStore((s) => s.checkpoint)
  const setCheckpoint = useGenerationFormStore((s) => s.setCheckpoint)
  const isGenerating = useGenerationFormStore((s) => s.isGenerating)

  const { data: models, isLoading, isError } = useAvailableModels(RESOURCE_TYPE.CHECKPOINT)

  const items = useMemo(
    () => (models ?? []).map(parseModelName),
    [models],
  )

  const selectedItem = useMemo(
    () => items.find((i) => i.id === checkpoint),
    [items, checkpoint],
  )

  const handleSelect = useCallback(
    (item: ModelItem) => { setCheckpoint(item.id) },
    [setCheckpoint],
  )

  const displayText = isLoading
    ? t('common.loading')
    : isError
      ? t('common.error')
      : selectedItem
        ? selectedItem.label
        : t('generate.selectCheckpoint')

  return (
    <div className="flex flex-col gap-1">
      <label
        className="text-xs font-medium text-tg-hint"
      >
        {t('generate.checkpoint')}
      </label>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => { setIsOpen(true) }}
        disabled={isGenerating || isLoading}
        className="flex w-full items-center justify-between rounded-lg border border-tg-hint/20 px-3 py-2.5 text-left text-sm outline-none transition-all duration-150 disabled:opacity-50 bg-tg-secondary hover:border-tg-hint/40 focus:border-[var(--tg-theme-button-color)] active:scale-[0.99]"
        style={{
          color: selectedItem ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
        }}
      >
        <span className="min-w-0 flex-1 truncate">{displayText}</span>
        <IconChevronRight className="ml-1 h-4 w-4 shrink-0 text-tg-hint transition-transform group-active:translate-x-0.5" />
      </button>

      {/* Full-screen Picker */}
      <Picker<ModelItem>
        isOpen={isOpen}
        onClose={() => { setIsOpen(false) }}
        onSelect={handleSelect}
        items={items}
        title={t('generate.checkpoint')}
        searchPlaceholder={t('generate.searchModel')}
        selectedId={checkpoint}
        isLoading={isLoading}
      />
    </div>
  )
}
